import {useEffect, useRef, useState} from "react";
import {Portal} from "@mui/material";
import {BrandingIcon} from "nav/pro/icons";
import {useMessages} from "i18n";
import {useLoginModal} from "login/LoginModalProvider";
import {useSuccessPersonalization} from "home/personalization/useSuccessPersonalization";
import type {PersonalizationDraftRequest} from "home/personalization/personalizationDraft";
import {companyFieldsOf, type SenderCompany} from "sender/senderChoice";
import {
	successPersonalizationEntry, successPersonalizationSettled,
} from "home/personalization/successPersonalization";
import {PersonaliseSheet} from "./PersonaliseSheet";
import {
	AuraAngleProperty, AuraWrap, PersonaliseEntry, Status, OfferPanel, OfferRow, OfferHeading,
	PreviewPair, PagePreview, PreviewCard, PreviewAvatar, PreviewTitle, PreviewLine,
	PreviewFile, PreviewButton, PreviewBand, OfferDetails,
} from "./SuccessPersonalization.styles";

export function SuccessPersonalization(
	{transferId, fileCount, attached, company}: {
		transferId: string;
		fileCount: number;
		/** The look the SEND carried, so the composer starts from the words the transfer already has. */
		attached: PersonalizationDraftRequest | null;
		/**
		 * The company the transfer went out as, or null for a person (kitecorp/transferify-ui#3186): the builder's
		 * miniature names it, and the look attached here goes without a name of its own.
		 */
		company: SenderCompany | null;
	},
) {
	const flow = useSuccessPersonalization(transferId, attached, company);
	const {sender} = useMessages();
	const {open: signIn} = useLoginModal();
	const opener = useRef<HTMLButtonElement>(null);
	const finalNotice = useRef<HTMLParagraphElement>(null);
	const [now, setNow] = useState(Date.now);
	useEffect(() => {
		if (flow.sendWindow?.expiresAtMs == null) return;
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [flow.sendWindow?.expiresAtMs]);
	const offered = flow.sendWindow !== null && successPersonalizationEntry({transferId,
		personalized: successPersonalizationSettled({stage: flow.stage, savedRung: flow.savedRung}),
		expiresAtMs: flow.sendWindow.expiresAtMs, now});
	const failed = flow.stage === "failed";
	// A refused look is not retried (`successAttachFailure`): the entry keeps its own label and reopens the builder.
	const retry = failed && flow.refusal === null;
	// The refusal's catalog key: the success screen's own sentence, or the refusal's own (`successAttachFailure`).
	const refusalSentence = flow.refusal === null ? null
		: flow.refusal === "personaliseRefused" ? sender.success.personaliseRefused : sender.errors[flow.refusal];
	const restoreFocus = () => (opener.current ?? finalNotice.current)?.focus();
	return <>
		{/* One compact row under the share row (kitecorp/transferify-ui#3320, P13): the example page, then the heading
			and the button. The standard page joins it, captions and all, where the card is wide enough. */}
		{(offered || failed) && <OfferPanel aria-label={sender.success.personaliseHeading}>
			<OfferRow>
				<PreviewPair>
					<PagePreview $standard>
						<PreviewCard aria-hidden="true">
							<PreviewAvatar/><PreviewTitle/>
							<PreviewLine/><PreviewFile/><PreviewFile/><PreviewButton/>
						</PreviewCard>
						<figcaption>{sender.success.standardPreview}</figcaption>
					</PagePreview>
					<PagePreview>
						<PreviewCard $example aria-hidden="true">
							<PreviewBand/><PreviewAvatar/>
							<PreviewTitle>{sender.success.previewTitle}</PreviewTitle>
							<PreviewLine/><PreviewFile/><PreviewFile/><PreviewButton/>
						</PreviewCard>
						<figcaption>{sender.success.personalisedPreview}</figcaption>
					</PagePreview>
				</PreviewPair>
				<OfferDetails>
					<OfferHeading>{sender.success.personaliseHeading}</OfferHeading>
					<AuraWrap>
						<AuraAngleProperty/>
						<PersonaliseEntry variant="secondary" type="button" ref={opener}
							data-testid="success-personalise-entry" disabled={flow.open || flow.busy}
							aria-label={retry ? sender.success.personaliseRetry : sender.success.personaliseAria}
							onClick={flow.show}>
							<BrandingIcon size={15}/>
							{retry ? sender.success.personaliseRetry : sender.success.personalise}
						</PersonaliseEntry>
					</AuraWrap>
				</OfferDetails>
			</OfferRow>
		</OfferPanel>}
		{flow.stage === "saving" && <Status role="status">{sender.success.personaliseSaving}</Status>}
		{flow.stage === "saved" && <Status role="status" tabIndex={-1} ref={finalNotice}>{sender.success.personaliseSaved}</Status>}
		{retry && <Status role="alert">{sender.success.personaliseFailed}</Status>}
		{failed && refusalSentence !== null && <Status role="alert">{refusalSentence}</Status>}
		{flow.stage === "expired" && <Status role="alert" tabIndex={-1} ref={finalNotice}>{sender.success.personaliseExpired}</Status>}
		{/* Portalled to body: the success card lives inside the gift frame's peel TRANSFORMS, which
		    make every ancestor a containing block — a fixed-position sheet rendered in place anchors
		    to the card and appears "stuck inside the success area" (owner report 2026-09-11). The
		    composer's own sheets never meet this because the create phase is not inside that frame. */}
		<Portal>
			<PersonaliseSheet open={flow.open} onClose={flow.close} onExited={restoreFocus}
				restoreFocusOnClose returnFocus={opener.current} vm={flow.vm} fileCount={fileCount}
				sentCompany={company === null ? null : companyFieldsOf(company)}
				onSignIn={signIn} onCommitted={flow.commit}/>
		</Portal>
	</>;
}
